import React from "react";
import { Link } from "react-router-dom";
import {
    Home as HomeIcon, ShieldCheck, BellRing, FileText, Key, CloudLightning, Hammer, ArrowRight, Check,
} from "lucide-react";

import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { SERVICES } from "@/lib/content";
import { useLanguage } from "@/lib/LanguageContext";
import useSEO from "@/lib/useSEO";

const ICON_MAP = { Home: HomeIcon, ShieldCheck, BellRing, FileText, Key, CloudLightning, Hammer };

export default function Services() {
    const { t, lang } = useLanguage();

    useSEO({
        title: lang === "es"
            ? "Servicios de Administración de Propiedades de Lujo | Pacific Key Management"
            : "Luxury Property Management Services | Pacific Key Management",
        description: lang === "es"
            ? "Explore administración de propiedades, supervisión de estates, administración financiera, preparación para huracanes y servicios personalizados en Los Cabos."
            : "Explore property management, estate oversight, financial administration, hurricane preparation and personalized homeowner services in Los Cabos.",
        path: "/services",
        keywords: "Luxury Property Management Los Cabos, High-End Property Management Cabo, Vacation Home Management Los Cabos",
        breadcrumbs: [
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
        ],
        schema: {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Luxury Property Management",
            provider: { "@type": "Organization", name: "Pacific Key Management", url: "https://pacifickeymanagement.com" },
            areaServed: { "@type": "Place", name: "Los Cabos, Baja California Sur, Mexico" },
            url: "https://pacifickeymanagement.com/services",
        },
    });

    return (
        <div data-testid="services-page">
            <PageHero
                eyebrow={t("services.heroEyebrow")}
                title={<>{t("services.heroTitle1")} <span className="italic font-light">{t("services.heroTitle2")}</span></>}
                subtitle={t("services.heroSubtitle")}
                image="https://images.unsplash.com/photo-1600573472550-8090b5e0745e?auto=format&fit=crop&w=2200&q=85"
            />

            <section className="bg-pearl py-24 sm:py-32">
                <div className="container-luxe space-y-28 sm:space-y-36">
                    {SERVICES.map((s, i) => {
                        const Icon = ICON_MAP[s.icon] || HomeIcon;
                        const reverse = i % 2 === 1;
                        const bullets = t(`services_data.${s.key}.bullets`);
                        const bulletList = Array.isArray(bullets) ? bullets : [];
                        return (
                            <Reveal key={s.key} className={`grid lg:grid-cols-12 gap-12 lg:gap-20 items-center ${reverse ? "lg:[direction:rtl]" : ""}`} data-testid={`service-detail-${s.key}`}>
                                <div className="lg:col-span-6 [direction:ltr]">
                                    <div className="aspect-[5/4] overflow-hidden">
                                        <img src={s.image} alt={t(`services_data.${s.key}.title`)} className="w-full h-full object-cover" loading="lazy" />
                                    </div>
                                </div>
                                <div className="lg:col-span-6 [direction:ltr] space-y-6">
                                    <Icon className="w-9 h-9 text-gold" strokeWidth={1.25} />
                                    <p className="overline text-gold">0{i + 1} · {t("services.sectionEyebrow")}</p>
                                    <h2 className="font-serif text-4xl sm:text-5xl leading-tight">{t(`services_data.${s.key}.title`)}</h2>
                                    <span className="gold-divider" />
                                    <p className="text-navy/75 leading-relaxed">{t(`services_data.${s.key}.summary`)}</p>
                                    <ul className="pt-4 space-y-3">
                                        {bulletList.map((b) => (
                                            <li key={b} className="flex items-start gap-3 text-navy/80 text-sm">
                                                <Check className="w-4 h-4 text-gold mt-1 flex-shrink-0" strokeWidth={1.5} />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            <section className="bg-sand/40 py-24 sm:py-32">
                <div className="container-luxe">
                    <Reveal className="max-w-3xl mb-16">
                        <p className="overline text-gold">{t("services.processEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("services.processTitle")}</h2>
                    </Reveal>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10">
                        {["p1", "p2", "p3", "p4"].map((k, i) => (
                            <Reveal key={k} delay={i * 90} className="bg-pearl p-10 min-h-[260px]" data-testid={`process-step-${i}`}>
                                <p className="text-gold font-serif text-3xl">0{i + 1}</p>
                                <h3 className="mt-5 font-serif text-2xl">{t(`services.${k}.title`)}</h3>
                                <span className="block w-8 h-px bg-gold my-4" />
                                <p className="text-sm text-navy/70 leading-relaxed">{t(`services.${k}.body`)}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-navy text-pearl py-24 text-center">
                <div className="container-luxe max-w-2xl mx-auto space-y-7">
                    <p className="overline text-gold">{t("services.outroEyebrow")}</p>
                    <h2 className="font-serif text-pearl text-4xl sm:text-5xl">{t("services.outroTitle")}</h2>
                    <p className="text-pearl/75">{t("services.outroBody")}</p>
                    <Link to="/contact" className="btn-luxe-light" data-testid="services-cta-contact">
                        {t("common.requestConsultation")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
